import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import {
  Home, Trophy, Sparkles, SlidersHorizontal, Library, Flame, User, Shield, Menu, LogOut, Plus, Wallet,
} from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Logo } from '@/components/Logo';
import { GlobalPlayer } from '@/components/player/GlobalPlayer';
import { useAuth } from '@/context/AuthContext';
import { LegalLinks } from '@/components/legal/LegalShell';
import { TermsAcceptanceModal } from '@/components/legal/TermsAcceptanceModal';
import { PendingDeletionNotice, isDeletionBlocking } from '@/components/account/DeletionPanel';

const NAV = [
  { to: '/app', label: 'Accueil', icon: Home, end: true },
  { to: '/challenges', label: 'Challenge Meilleure Musique', icon: Trophy },
  { to: '/create', label: 'Créer', icon: Sparkles },
  { to: '/studio', label: 'Studio', icon: SlidersHorizontal },
  { to: '/library', label: 'Bibliothèque', icon: Library },
  { to: '/hits', label: 'Meilleurs Hits', icon: Flame },
  { to: '/credits', label: 'Crédits', icon: Wallet },
  { to: '/account', label: 'Mon compte', icon: User },
];

function NavItems({ onNavigate, isAdmin }) {
  const items = [...NAV];
  if (isAdmin) items.push({ to: '/admin', label: 'Administration', icon: Shield });
  return (
    <nav className="flex flex-col gap-1">
      {items.map((it) => (
        <NavLink
          key={it.to}
          to={it.to}
          end={it.end}
          onClick={onNavigate}
          data-testid={`nav-${it.to === '/' ? 'home' : it.to.slice(1)}`}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-all ${
              isActive
                ? 'bg-brand/15 text-brand'
                : 'text-muted-foreground hover:bg-white/5 hover:text-white'
            }`
          }
        >
          <it.icon className="h-5 w-5 flex-shrink-0" />
          <span className="truncate">{it.label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

function UserBox() {
  const { profile, signOut, session } = useAuth();
  const navigate = useNavigate();
  if (!session) {    return (
      <div className="flex flex-col gap-2">
        <button onClick={() => navigate('/login')} data-testid="sidebar-login"
                className="rounded-full bg-brand py-2.5 text-sm font-semibold text-black btn-glow hover:bg-brand-hover">
          Se connecter
        </button>
        <button onClick={() => navigate('/register')} data-testid="sidebar-register"
                className="rounded-full bg-surface-card py-2.5 text-sm font-medium text-white hover:bg-surface-cardHover">
          Créer un compte
        </button>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <button onClick={() => navigate('/credits')} data-testid="sidebar-credits"
              className="flex w-full items-center justify-between rounded-xl border border-brand/30 bg-brand/10 px-3 py-2 text-sm hover:bg-brand/15">
        <span className="flex items-center gap-2 text-white"><Wallet className="h-4 w-4 text-brand" /> Crédits</span>
        <span className="font-display font-bold text-brand" data-testid="sidebar-credits-balance">{profile?.credits_balance ?? 0}</span>
      </button>
      <div className="flex items-center gap-3 rounded-xl bg-surface-card p-2.5">
        <div className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full bg-surface-cardHover text-sm font-bold text-white">
          {profile?.avatar_url ? <img src={profile.avatar_url} alt="" className="h-full w-full object-cover" /> : (profile?.display_name?.[0] || 'U').toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-white">{profile?.display_name || 'Artiste'}</p>
          <p className="truncate text-xs text-muted-foreground">{profile?.is_premium ? 'Premium' : 'Essai gratuit'}</p>
        </div>
        <button onClick={() => { signOut(); navigate('/'); }} data-testid="sidebar-logout"
                className="text-muted-foreground hover:text-white" title="Se déconnecter">
          <LogOut className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export function Layout() {
  const { isAdmin, profile } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const suspended = isDeletionBlocking(profile?.deletion);

  return (
    <div className="min-h-screen">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-72 flex-col border-r border-border bg-surface-sidebar p-5 lg:flex">
        <Logo variant="full" className="h-14" to="/app" />
        <button onClick={() => navigate('/create')} data-testid="sidebar-new-song"
                className="mt-6 flex items-center justify-center gap-2 rounded-full bg-brand py-2.5 text-sm font-semibold text-black btn-glow hover:bg-brand-hover">
          <Plus className="h-4 w-4" /> Nouveau morceau
        </button>
        <div className="mt-6 flex-1 overflow-y-auto no-scrollbar">
          <NavItems isAdmin={isAdmin} />
        </div>
        <UserBox />
        <LegalLinks className="mt-3 px-1" />
      </aside>

      {/* Mobile header */}
      <header className="sticky top-0 z-40 flex items-center justify-between border-b border-border glass px-4 py-3 lg:hidden">
        <Logo variant="emblem" className="h-10" to="/app" />
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button className="flex h-10 w-10 items-center justify-center rounded-full bg-surface-card text-white" data-testid="mobile-menu-button">
              <Menu className="h-5 w-5" />
            </button>
          </SheetTrigger>
          <SheetContent side="left" className="w-80 border-border bg-surface-sidebar p-5">
            <div className="mb-6"><Logo variant="full" className="h-12" to="/app" /></div>
            <div className="mb-6"><UserBox /></div>
            <NavItems isAdmin={isAdmin} onNavigate={() => setOpen(false)} />
            <LegalLinks className="mt-6 px-1" />
          </SheetContent>
        </Sheet>
      </header>

      {/* Main */}
      <main className="lg:pl-72">
        <div className="mx-auto max-w-[1600px] px-4 pb-32 pt-6 sm:px-6 lg:px-10">
          {suspended ? (
            <div className="mx-auto max-w-xl pt-10" data-testid="account-suspended"><PendingDeletionNotice /></div>
          ) : <Outlet />}
        </div>
      </main>

      <TermsAcceptanceModal />
      <GlobalPlayer />
    </div>
  );
}
